import React from 'react';
import { Box, Chip, Typography, CircularProgress, LinearProgress } from '@mui/material';
import { C, T } from './leadOverviewCardTokens';
import { ClosedCard, OverviewCardTitleWithIcon } from './overviewCardPrimitives';

function LeadRecoveryScoreDonut({ value }) {
  const v = value != null ? Math.round(Math.max(0, Math.min(100, Number(value)))) : null;
  const pct = v != null ? v : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
      <Box sx={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={80}
          thickness={5}
          sx={{ color: '#FFEDD5', position: 'absolute', left: 0, top: 0 }}
        />
        <CircularProgress
          variant="determinate"
          value={pct}
          size={80}
          thickness={5}
          sx={{
            color: '#EA580C',
            position: 'absolute',
            left: 0,
            top: 0,
            transform: 'rotate(-90deg)',
            '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography sx={{ fontSize: '22px', fontWeight: 900, color: C.scoreBrown, lineHeight: 1 }}>{v != null ? v : '—'}</Typography>
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '28px', fontWeight: 800, color: C.scoreBrown, lineHeight: 1.1 }}>
          {v != null ? `${v}/100` : '—/100'}
        </Typography>
        <Typography sx={{ ...T.bodyPrimary, fontWeight: 600, color: C.slate3, mt: 0.5 }}>Recovery score</Typography>
      </Box>
    </Box>
  );
}

function LeadRecoveryReadinessBars({ readinessRows, fallbackAi, fallbackRule }) {
  const rows =
    Array.isArray(readinessRows) && readinessRows.length > 0
      ? readinessRows.slice(0, 2).map((row, i) => ({
          label: row.label || (i === 0 ? 'Signal' : 'Engagement'),
          score: Number(row.score),
        }))
      : [
          { label: 'AI score', score: fallbackAi != null ? Number(fallbackAi) : null },
          { label: 'Rule score', score: fallbackRule != null ? Number(fallbackRule) : null },
        ];
  return (
    <Box sx={{ display: 'grid', gap: 1.25, mb: 1.5 }}>
      {rows.map((row, i) => {
        const n = row.score != null && !Number.isNaN(row.score) ? Math.round(Math.max(0, Math.min(100, row.score))) : null;
        return (
          <Box key={`${row.label}-${i}`}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, gap: 1 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: C.slate2 }}>{row.label}</Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 800, color: C.slate2 }}>{n != null ? n : '—'}</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={n != null ? n : 0}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: '#EEF2FF',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: i === 0 ? C.blue : '#EA580C',
                },
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

function _ctxStr(v) {
  if (v == null) return '';
  const s = typeof v === 'string' ? v.trim() : String(v).trim();
  return s;
}

/** Budget / location / requirement from `lead_recovery` (last assessed call insights). */
function LeadRecoveryInsightContext({ niRecovery }) {
  if (!niRecovery) return null;
  const budget = _ctxStr(niRecovery.budget_range ?? niRecovery.budgetRange);
  const requirement = _ctxStr(niRecovery.requirement ?? niRecovery.requirements);
  const location = _ctxStr(niRecovery.location);
  if (!budget && !requirement && !location) return null;

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: '12px',
        border: '1px solid #C7D2FE',
        bgcolor: '#EEF2FF',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(67, 56, 202, 0.06)',
      }}
    >
      <Box
        sx={{
          px: '14px',
          py: '10px',
          background: 'linear-gradient(180deg, #E0E7FF 0%, #EEF2FF 100%)',
          borderBottom: '1px solid #C7D2FE',
        }}
      >
        <Typography sx={{ ...T.cardHeader, color: '#3730A3', letterSpacing: '0.07em', mb: 0 }}>
          Lead requirements
        </Typography>
        
      </Box>
      <Box sx={{ p: '14px', display: 'grid', gap: 1.75 }}>
        {budget ? (
          <Box>
            <Typography sx={{ ...T.cardHeader, color: C.slate3, mb: '8px' }}>Price range</Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                bgcolor: '#FFFBEB',
                border: '1px solid #F59E0B',
                boxShadow: '0 1px 2px rgba(245, 158, 11, 0.12)',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: '15px', sm: '17px' },
                  fontWeight: 900,
                  color: '#92400E',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                {budget}
              </Typography>
            </Box>
          </Box>
        ) : null}

        {location ? (
          <Box
            sx={{
              p: '12px 14px',
              borderRadius: '10px',
              bgcolor: 'rgba(255,255,255,0.85)',
              border: `1px solid ${C.border}`,
            }}
          >
            <Typography sx={{ ...T.cardHeader, color: C.slate3, mb: '8px' }}>Location focus</Typography>
            <Typography sx={{ ...T.bodyPrimary, fontWeight: 700, color: C.slate2, lineHeight: 1.5 }}>{location}</Typography>
          </Box>
        ) : null}

        {requirement ? (
          <Box
            sx={{
              p: '12px 14px',
              borderRadius: '10px',
              bgcolor: 'rgba(255,255,255,0.92)',
              border: `1px solid ${C.border}`,
              borderLeft: '4px solid #4F46E5',
            }}
          >
            <Typography sx={{ ...T.cardHeader, color: '#4338CA', mb: '8px', letterSpacing: '0.06em' }}>
              What they shared
            </Typography>
            <Typography
              sx={{
                ...T.bodyPrimary,
                fontWeight: 500,
                color: C.slate2,
                lineHeight: 1.55,
                fontSize: '12px',
              }}
            >
              {requirement}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function LeadRecoveryBody({
  recoveryScore,
  recoverable,
  niRecovery,
  isNotInterested,
  isJunkOrArchived,
  conversionReadiness,
  recoveryBarsFallbackAi,
  recoveryBarsFallbackRule,
}) {
  return (
    <>
      <LeadRecoveryScoreDonut value={recoveryScore} />
      {niRecovery ? (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            size="small"
            label={(
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: recoverable ? C.green : '#F59E0B',
                    flexShrink: 0,
                  }}
                />
                {recoverable ? 'Recovery eligible' : 'Review eligibility'}
              </Box>
            )}
            sx={{
              height: 28,
              fontWeight: 800,
              fontSize: '0.7rem',
              bgcolor: recoverable ? C.greenLight : '#FFFBEB',
              color: recoverable ? C.greenDark : '#92400E',
              border: `1px solid ${recoverable ? '#86EFAC' : '#FDE68A'}`,
              '& .MuiChip-label': { px: 1.25 },
            }}
          />
        </Box>
      ) : isJunkOrArchived ? (
        <Typography sx={{ ...T.bodySecondary, mb: 1.5, fontStyle: 'italic', color: C.slate4, fontSize: '12px' }}>
          Recovery scoring and evidence are generated when a lead is marked not interested.
        </Typography>
      ) : null}

      <LeadRecoveryReadinessBars
        readinessRows={conversionReadiness}
        fallbackAi={recoveryBarsFallbackAi}
        fallbackRule={recoveryBarsFallbackRule}
      />

      <LeadRecoveryInsightContext niRecovery={niRecovery} />

      {isNotInterested ? (
        <Box sx={{ bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', p: '12px 14px', mb: 1.5 }}>
          <Typography sx={{ ...T.cardHeader, color: '#991B1B', mb: 0.75, letterSpacing: '0.06em' }}>Not interested reason</Typography>
          <Typography sx={{ ...T.bodyPrimary, fontWeight: 500, color: C.slate2 }}>
            {niRecovery?.reason_category ? `${niRecovery.reason_category} — ` : ''}
            {String(niRecovery?.reason || '').trim() || 'No detailed reason logged.'}
          </Typography>
        </Box>
      ) : null}

      {(niRecovery?.recovery_evidence || []).length ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ ...T.cardHeader, mb: 1 }}>Recovery evidence</Typography>
          <Box sx={{ display: 'grid', gap: '10px' }}>
            {(niRecovery.recovery_evidence || []).slice(0, 6).map((e, i) => (
              <Box key={i} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '999px', bgcolor: '#1F9D6A', mt: '7px', flexShrink: 0 }} />
                <Typography sx={{ ...T.bodyPrimary, lineHeight: 1.45, fontWeight: 500 }}>{e}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {niRecovery?.recovery_strategy ? (
        <Box sx={{ bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', p: '12px 14px' }}>
          <Typography sx={{ ...T.cardHeader, color: C.greenDark, mb: 0.75, letterSpacing: '0.06em' }}>Next recovery strategy</Typography>
          <Typography sx={{ ...T.bodyPrimary, fontWeight: 500, color: C.slate2 }}>{niRecovery.recovery_strategy}</Typography>
        </Box>
      ) : null}
    </>
  );
}

export default function LeadRecovery({
  variant = 'closed',
  headerRight,
  recoveryScore,
  recoverable,
  niRecovery,
  isNotInterested,
  isJunkOrArchived,
  conversionReadiness,
  recoveryBarsFallbackAi,
  recoveryBarsFallbackRule,
  niActionFooter,
}) {
  const bodyProps = {
    recoveryScore,
    recoverable,
    niRecovery,
    isNotInterested,
    isJunkOrArchived,
    conversionReadiness,
    recoveryBarsFallbackAi,
    recoveryBarsFallbackRule,
  };

  if (variant === 'executive') {
    return (
      <Box
        sx={{
          bgcolor: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: '14px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          p: '18px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.25, flexShrink: 0 }}>
          <OverviewCardTitleWithIcon icon="🔄" iconBg="#DBEAFE" title="Lead recovery" />
          {headerRight ? <Box sx={{ flexShrink: 0 }}>{headerRight}</Box> : null}
        </Box>
        <LeadRecoveryBody {...bodyProps} />
        {niActionFooter}
      </Box>
    );
  }

  return (
    <ClosedCard title="Lead recovery" iconEmoji="🔄" iconBg="#DBEAFE" headerIconSize={28} right={headerRight}>
      <LeadRecoveryBody {...bodyProps} />
      {niActionFooter}
    </ClosedCard>
  );
}
